import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import {
  MessageSquare, Send, Plus, X, Building2, Globe, Target
} from 'lucide-react';

const SuperAdminCommunications = () => {
  const { api } = useAuth();
  const { t, formatDate } = useLanguage();
  const [comms, setComms] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    type: 'notification', subject: '', message: '', target_type: 'all', target_company_ids: []
  });

  const fetchComms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/super-admin/communications');
      setComms(res.data.communications);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api]);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/companies?limit=200');
      setCompanies(res.data.companies);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => { fetchComms(); fetchCompanies(); }, [fetchComms, fetchCompanies]);

  const handleSend = async (e) => {
    e.preventDefault();
    try {
      await api.post('/super-admin/communications', formData);
      setShowCreate(false);
      setFormData({ type: 'notification', subject: '', message: '', target_type: 'all', target_company_ids: [] });
      fetchComms();
    } catch (err) { alert(err.response?.data?.detail || t('saComm.errorGeneric')); }
  };

  const TYPE_ICONS = {
    notification: { icon: Globe, cls: 'bg-blue-500/15 text-blue-400' },
    maintenance: { icon: Target, cls: 'bg-amber-500/15 text-amber-400' },
    update: { icon: MessageSquare, cls: 'bg-[#009b3a]/15 text-[#009b3a]' },
  };

  // Libellés d'affichage pour les valeurs de type renvoyées par le backend
  // (la clé métier reste brute côté logique ; repli sur la valeur brute).
  const typeLabel = (type) => {
    const labels = {
      notification: t('saComm.typeNotification'),
      maintenance: t('saComm.typeMaintenance'),
      update: t('saComm.typeUpdate'),
    };
    return labels[type] || type;
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-4" data-testid="super-admin-communications">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-white">{t('saComm.title')}</h1>
            <p className="text-sm text-gray-500">{t('saComm.messagesSent', { count: total })}</p>
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="new-communication-btn" className="flex items-center gap-2 px-4 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium hover:bg-[#00b344] transition-colors">
            <Plus className="w-4 h-4" /> {t('saComm.newMessage')}
          </button>
        </div>

        {/* List */}
        <div className="space-y-2">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="w-6 h-6 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : comms.length === 0 ? (
            <div className="bg-[#111827] border border-white/5 rounded-xl p-12 text-center">
              <MessageSquare className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{t('saComm.emptyList')}</p>
            </div>
          ) : (
            comms.map(c => {
              const typeInfo = TYPE_ICONS[c.type] || TYPE_ICONS.notification;
              const TypeIcon = typeInfo.icon;
              return (
                <div key={c.id} className="bg-[#111827] border border-white/5 rounded-xl p-4" data-testid={`comm-${c.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${typeInfo.cls}`}>
                      <TypeIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-white truncate">{c.subject || t('saComm.noSubject')}</h3>
                        <span className="text-[10px] text-gray-600 flex-shrink-0">
                          {formatDate(c.created_at, { dateStyle: 'short', timeStyle: 'short' })}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{c.message}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-500">
                          {c.target_type === 'all' ? t('saComm.allClients') : t('saComm.companiesCount', { count: c.target_company_ids?.length || 0 })}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-gray-500">{typeLabel(c.type)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Send Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111827] border border-white/10 rounded-2xl w-full max-w-lg" data-testid="send-comm-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h2 className="text-base font-bold text-white">{t('saComm.modalTitle')}</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded-lg hover:bg-white/5">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleSend} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('saComm.typeLabel')}</label>
                  <select value={formData.type} onChange={e => setFormData(p => ({...p, type: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none">
                    <option value="notification">{t('saComm.typeNotification')}</option>
                    <option value="maintenance">{t('saComm.typeMaintenance')}</option>
                    <option value="update">{t('saComm.typeUpdate')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('saComm.recipients')}</label>
                  <select value={formData.target_type} onChange={e => setFormData(p => ({...p, target_type: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none">
                    <option value="all">{t('saComm.allClients')}</option>
                    <option value="selected">{t('saComm.targetSelected')}</option>
                  </select>
                </div>
              </div>
              {formData.target_type === 'selected' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('saComm.targetedCompanies')}</label>
                  <div className="max-h-32 overflow-y-auto bg-[#0a0f1c] border border-white/10 rounded-lg p-2 space-y-1">
                    {companies.map(c => (
                      <label key={c.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white/5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.target_company_ids.includes(c.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setFormData(p => ({...p, target_company_ids: [...p.target_company_ids, c.id]}));
                            } else {
                              setFormData(p => ({...p, target_company_ids: p.target_company_ids.filter(id => id !== c.id)}));
                            }
                          }}
                          className="rounded border-gray-600"
                        />
                        <span className="text-xs text-gray-300">{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saComm.subjectLabel')}</label>
                <input required value={formData.subject} onChange={e => setFormData(p => ({...p, subject: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none" data-testid="input-comm-subject" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('saComm.messageLabel')}</label>
                <textarea required value={formData.message} onChange={e => setFormData(p => ({...p, message: e.target.value}))} rows={4} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none resize-none" data-testid="input-comm-message" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2.5 text-sm text-gray-400">{t('saComm.cancel')}</button>
                <button type="submit" data-testid="submit-send-comm" className="flex items-center gap-2 px-5 py-2.5 bg-[#009b3a] text-white rounded-lg text-sm font-medium hover:bg-[#00b344]">
                  <Send className="w-4 h-4" /> {t('saComm.send')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default SuperAdminCommunications;
